import {
  minutes,
  type AssignmentId,
  type ISODateTime,
  type Minutes,
  type ResourceId,
  type TaskId,
  type Units,
} from '../domain/branded.js';
import type { Assignment } from '../domain/assignment.js';
import type { Project } from '../domain/project.js';
import type { Resource } from '../domain/resource.js';
import type { Task } from '../domain/task.js';
import type { Diagnostic } from '../domain/errors.js';
import {
  addWorkingMinutes,
  snapToNextWorkingTime,
  workingMinutesBetween,
} from './calendar-engine.js';
import { formatDateTime, parseDateTime } from './calendar-time.js';
import {
  applySchedule,
  schedule,
  type ScheduleResult,
  type ScheduledTask,
  type TaskSegment,
} from './scheduling.js';
import {
  buildPlacements,
  findOverallocations,
  windowsFromProject,
  CalendarSet,
  OVERALLOCATION_EPSILON,
  type Placement,
  type TaskWindows,
} from './resource-timephased.js';

export type {
  AssignmentTimephased,
  Placement,
  TaskWindows,
  TimephasedResult,
} from './resource-timephased.js';
export {
  availableUnitsOn,
  buildPlacements,
  computeTimephasedWork,
  windowsFromProject,
  CalendarSet,
} from './resource-timephased.js';

/**
 * 資源撫平引擎契約。實作者：leveling agent。
 */

/** 撫平時決定「誰先讓路」的排序準則。 */
export type LevelingOrder =
  /** 僅依任務 id 順序。 */
  | 'Id'
  /** MS Project Standard：先看相依、浮時、日期、優先權、id。 */
  | 'Standard'
  /** 先看優先權，再走 Standard 準則。 */
  | 'PriorityStandard';

/** 撫平的時間解析度。 */
export type LevelingGranularity = 'Minute' | 'Hour' | 'Day' | 'Week' | 'Month';

export interface LevelingOptions {
  readonly order: LevelingOrder;
  readonly granularity: LevelingGranularity;
  /** 只在浮時範圍內撫平，不延後專案完成日。 */
  readonly levelWithinSlack: boolean;
  /** 允許切分任務以消除衝突。 */
  readonly canSplitTasks: boolean;
  /** 允許個別調整指派（而非整個任務一起移動）。 */
  readonly canLevelAssignments: boolean;
  /** 只撫平指定資源；空陣列或未提供代表全部。 */
  readonly onlyResourceIds?: readonly ResourceId[];
  /** 只撫平此期間內的配置；未提供代表全期間。 */
  readonly period?: { readonly from: ISODateTime; readonly to: ISODateTime };
  /** 撫平前先清除既有的 levelingDelay。 */
  readonly clearPreviousLeveling: boolean;
  /** 優先權大於等於此值的任務不撫平；MS Project 預設 1000。 */
  readonly doNotLevelPriorityThreshold: number;
  /** 保護已開始的任務（有 actualStart 者不移動）。 */
  readonly preserveStartedTasks: boolean;
}

export interface LevelingInput {
  readonly project: Project;
  /** 撫平前的排程結果；未提供時實作應自行先跑一次 schedule()。 */
  readonly schedule?: ScheduleResult;
  readonly options: LevelingOptions;
}

/** 單一任務因撫平而產生的調整。 */
export interface LevelingAdjustment {
  readonly taskId: TaskId;
  /** 相對原始 early start 的延遲量。 */
  readonly levelingDelay: Minutes;
  readonly newStart: ISODateTime;
  readonly newFinish: ISODateTime;
  /** 切分後的區段；未切分則為單一段。 */
  readonly segments: readonly TaskSegment[];
}

/** 指派層級的調整（canLevelAssignments = true 時才會出現）。 */
export interface AssignmentLevelingAdjustment {
  readonly assignmentId: AssignmentId;
  readonly levelingDelay: Minutes;
  readonly newUnits: Units;
}

/** 仍然存在的超額配置。 */
export interface Overallocation {
  readonly resourceId: ResourceId;
  readonly from: ISODateTime;
  readonly to: ISODateTime;
  /** 該區間內的需求單位總和。 */
  readonly demandUnits: Units;
  /** 該區間內的可用單位。 */
  readonly capacityUnits: Units;
  /** 涉及的任務。 */
  readonly taskIds: readonly TaskId[];
}

export interface LevelingResult {
  readonly adjustments: readonly LevelingAdjustment[];
  readonly assignmentAdjustments: readonly AssignmentLevelingAdjustment[];
  /** 撫平後仍無法消除的超額配置。 */
  readonly remainingOverallocations: readonly Overallocation[];
  /** 撫平後的專案完成日。 */
  readonly projectFinish: ISODateTime;
  /** 相對撫平前的完成日延後量。 */
  readonly finishDelay: Minutes;
  readonly diagnostics: readonly Diagnostic[];
}

/** MS Project 的預設撫平選項（順序 Standard、以日為粒度、不限制浮時）。 */
export const DEFAULT_LEVELING_OPTIONS: LevelingOptions = {
  order: 'Standard',
  granularity: 'Day',
  levelWithinSlack: false,
  canSplitTasks: false,
  canLevelAssignments: false,
  clearPreviousLeveling: true,
  doNotLevelPriorityThreshold: 1000,
  preserveStartedTasks: true,
};

// ---------------------------------------------------------------------------
// 時間點比較（wall-clock 字串，分鐘粒度；不做任何日期運算）
// ---------------------------------------------------------------------------

const cmpKey = (at: ISODateTime): string => (at as string).slice(0, 16);
const isBefore = (a: ISODateTime, b: ISODateTime): boolean => cmpKey(a) < cmpKey(b);
const isAfter = (a: ISODateTime, b: ISODateTime): boolean => cmpKey(a) > cmpKey(b);
const later = (a: ISODateTime, b: ISODateTime): ISODateTime => (isBefore(a, b) ? b : a);

// ---------------------------------------------------------------------------
// 迭代上限
// ---------------------------------------------------------------------------

/**
 * 撫平的迭代上限。
 *
 * 每一輪都會替「每個仍衝突的資源」推遲一個任務，因此收斂所需的輪數約為
 * 「最長的資源競爭鏈長度」，上界為任務數。這裡取 `4 × 任務數` 留出餘裕
 * （同一任務可能因不同資源被反覆推遲），再夾在 [50, 1000] 之間：下限確保
 * 小專案的病態案例也有足夠嘗試次數，上限確保大專案不會退化成長時間空轉。
 */
export function levelingIterationLimit(taskCount: number): number {
  return Math.max(50, Math.min(1000, taskCount * 4));
}

// ---------------------------------------------------------------------------
// 內部工具
// ---------------------------------------------------------------------------

/** 可被撫平延後的限制條件；其餘限制一旦改寫為 SNET 就會遺失語義。 */
const MOVABLE_CONSTRAINTS = new Set(['ASAP', 'SNET']);

interface Context {
  readonly project: Project;
  readonly options: LevelingOptions;
  readonly calendars: CalendarSet;
  readonly tasksById: ReadonlyMap<string, Task>;
  readonly resourcesById: ReadonlyMap<string, Resource>;
  readonly assignmentsByTask: ReadonlyMap<string, readonly Assignment[]>;
  /** key: `${predecessorId}>${successorId}` */
  readonly directEdges: ReadonlySet<string>;
  readonly resourceFilter: ReadonlySet<string> | null;
}

function buildContext(project: Project, options: LevelingOptions): Context {
  const tasksById = new Map<string, Task>();
  for (const task of project.tasks) tasksById.set(task.id as string, task);
  const resourcesById = new Map<string, Resource>();
  for (const resource of project.resources) resourcesById.set(resource.id as string, resource);
  const assignmentsByTask = new Map<string, Assignment[]>();
  for (const assignment of project.assignments) {
    const key = assignment.taskId as string;
    const bucket = assignmentsByTask.get(key);
    if (bucket === undefined) assignmentsByTask.set(key, [assignment]);
    else bucket.push(assignment);
  }
  const directEdges = new Set<string>();
  for (const dependency of project.dependencies) {
    directEdges.add(`${dependency.predecessorId as string}>${dependency.successorId as string}`);
  }
  const filterIds = options.onlyResourceIds;
  const resourceFilter =
    filterIds === undefined || filterIds.length === 0
      ? null
      : new Set(filterIds.map((value) => value as string));
  return {
    project,
    options,
    calendars: new CalendarSet(project),
    tasksById,
    resourcesById,
    assignmentsByTask,
    directEdges,
    resourceFilter,
  };
}

/** 任務是否可以被撫平延後。 */
function isMovable(task: Task, options: LevelingOptions): boolean {
  if (task.isSummary) return false;
  if (task.isManuallyScheduled) return false;
  if (!MOVABLE_CONSTRAINTS.has(task.constraintType)) return false;
  if (task.priority >= options.doNotLevelPriorityThreshold) return false;
  if (options.preserveStartedTasks && task.actualStart !== null) return false;
  return true;
}

/** 以 SNET 限制把任務釘在指定的最早開始時間，交給 schedule() 重新排程。 */
function withForcedStarts(
  project: Project,
  forced: ReadonlyMap<string, ISODateTime>,
): Project {
  if (forced.size === 0) return project;
  const tasks = project.tasks.map((task) => {
    const at = forced.get(task.id as string);
    if (at === undefined) return task;
    const constraintDate =
      task.constraintType === 'SNET' && task.constraintDate !== null
        ? later(task.constraintDate, at)
        : at;
    const next: Task = { ...task, constraintType: 'SNET', constraintDate };
    return next;
  });
  return { ...project, tasks };
}

const scheduleMap = (result: ScheduleResult): ReadonlyMap<string, ScheduledTask> => {
  const map = new Map<string, ScheduledTask>();
  for (const entry of result.tasks) map.set(entry.taskId as string, entry);
  return map;
};

function windowsFromSchedule(
  result: ScheduleResult,
  splits: ReadonlyMap<string, readonly TaskSegment[]>,
): TaskWindows {
  const windows = new Map<string, readonly TaskSegment[]>();
  for (const entry of result.tasks) {
    const key = entry.taskId as string;
    const split = splits.get(key);
    windows.set(key, split ?? [{ start: entry.start, finish: entry.finish }]);
  }
  return windows;
}

/** 過濾掉不在 onlyResourceIds / period 範圍內的超額配置。 */
function filterOverallocations(
  context: Context,
  all: readonly Overallocation[],
): readonly Overallocation[] {
  const period = context.options.period;
  return all.filter((entry) => {
    if (context.resourceFilter !== null && !context.resourceFilter.has(entry.resourceId as string)) {
      return false;
    }
    if (period !== undefined) {
      if (!isBefore(entry.from, period.to)) return false;
      if (!isAfter(entry.to, period.from)) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// 排序準則
// ---------------------------------------------------------------------------

interface Contender {
  readonly task: Task;
  /** 目前這一輪的排程結果；用於幾何計算（游標、延後量、剩餘浮時）。 */
  readonly scheduled: ScheduledTask;
  /**
   * **撫平前**的排程結果；只用於排序準則。
   *
   * 排序鍵必須在整個撫平過程中固定：若改用每輪重算的浮時，專案完成日一被推遲，
   * 所有任務的浮時就跟著放大、名次翻轉，讓路者與存活者會在下一輪互換角色而彼此
   * 反覆往後推（每輪各推一天），演算法退化成不收斂的振盪。
   */
  readonly ranking: ScheduledTask;
}

/**
 * 「誰先讓路」的排序：回傳的陣列中，**排在前面的任務保持不動**，
 * 排在最後的任務最先被延後。
 */
function sortContenders(context: Context, contenders: Contender[]): Contender[] {
  const order = context.options.order;
  const byPredecessor = (a: Contender, b: Contender): number => {
    const aFirst = context.directEdges.has(`${a.task.id as string}>${b.task.id as string}`);
    const bFirst = context.directEdges.has(`${b.task.id as string}>${a.task.id as string}`);
    if (aFirst && !bFirst) return -1;
    if (bFirst && !aFirst) return 1;
    return 0;
  };
  const standard = (a: Contender, b: Contender): number => {
    const predecessor = byPredecessor(a, b);
    if (predecessor !== 0) return predecessor;
    // 浮時少者留下（越接近要徑越不該動）；以撫平前的浮時為準，確保名次穩定。
    const float = (a.ranking.totalFloat as number) - (b.ranking.totalFloat as number);
    if (float !== 0) return float;
    // 開始日早者留下。
    if (isBefore(a.ranking.start, b.ranking.start)) return -1;
    if (isAfter(a.ranking.start, b.ranking.start)) return 1;
    // 優先權高者留下。
    if (a.task.priority !== b.task.priority) return b.task.priority - a.task.priority;
    return (a.task.id as string) < (b.task.id as string) ? -1 : 1;
  };
  const comparator = (a: Contender, b: Contender): number => {
    switch (order) {
      case 'Id':
        return (a.task.id as string) < (b.task.id as string) ? -1 : 1;
      case 'PriorityStandard': {
        if (a.task.priority !== b.task.priority) return b.task.priority - a.task.priority;
        return standard(a, b);
      }
      case 'Standard':
      default:
        return standard(a, b);
    }
  };
  return [...contenders].sort(comparator);
}

// ---------------------------------------------------------------------------
// 撫平主流程
// ---------------------------------------------------------------------------

interface RoundOutcome {
  readonly progressed: boolean;
  readonly blocked: readonly string[];
}

function resolveRound(
  context: Context,
  overallocations: readonly Overallocation[],
  byId: ReadonlyMap<string, ScheduledTask>,
  baseById: ReadonlyMap<string, ScheduledTask>,
  forced: Map<string, ISODateTime>,
): RoundOutcome {
  const handledResources = new Set<string>();
  const touchedTasks = new Set<string>();
  const blocked: string[] = [];
  let progressed = false;

  const sorted = [...overallocations].sort((a, b) => {
    if (isBefore(a.from, b.from)) return -1;
    if (isAfter(a.from, b.from)) return 1;
    return (a.resourceId as string) < (b.resourceId as string) ? -1 : 1;
  });

  for (const entry of sorted) {
    const resourceKey = entry.resourceId as string;
    if (handledResources.has(resourceKey)) continue;

    const contenders: Contender[] = [];
    for (const taskId of entry.taskIds) {
      const task = context.tasksById.get(taskId as string);
      const scheduled = byId.get(taskId as string);
      if (task === undefined || scheduled === undefined) continue;
      contenders.push({ task, scheduled, ranking: baseById.get(taskId as string) ?? scheduled });
    }
    if (contenders.length < 2) {
      blocked.push(resourceKey);
      continue;
    }
    const ranked = sortContenders(context, contenders);

    // 排名第一者不動；只有在其餘競爭者全都不可撫平時，才退而移動第一名
    // （否則衝突永遠無解）。
    const othersMovable = ranked
      .slice(1)
      .some((contender) => isMovable(contender.task, context.options));

    // 由排名最後者開始挑選讓路者，直到需求降到可用量以下為止
    // （只挑「足夠消除衝突」的數量，避免把不必要的任務也一起推遲）。
    const capacity = entry.capacityUnits as number;
    let demand = entry.demandUnits as number;
    const losers: Contender[] = [];
    const loserIds = new Set<string>();
    for (
      let index = ranked.length - 1;
      index >= 0 && demand > capacity + OVERALLOCATION_EPSILON;
      index -= 1
    ) {
      const contender = ranked[index];
      if (contender === undefined) continue;
      if (index === 0 && othersMovable) break;
      const key = contender.task.id as string;
      if (touchedTasks.has(key)) continue;
      if (!isMovable(contender.task, context.options)) continue;
      losers.unshift(contender);
      loserIds.add(key);
      demand -= unitsOnResource(context, contender.task.id as string, entry.resourceId);
    }

    // 讓路者依排名先後接續排在留下來的任務之後（一輪內即完成串接）。
    // 必須取存活競爭者中「最晚」的完成時間：只接在最早完成者之後，讓路者仍會與
    // 其他尚未結束的存活者重疊，下一輪又得再推一次，收斂會明顯變慢。
    let cursor: ISODateTime | null = null;
    for (const contender of ranked) {
      if (loserIds.has(contender.task.id as string)) continue;
      const finish = contender.scheduled.finish;
      if (!isAfter(finish, entry.from)) continue;
      if (!isBefore(contender.scheduled.start, entry.to)) continue;
      cursor = cursor === null || isAfter(finish, cursor) ? finish : cursor;
    }
    if (cursor === null) cursor = entry.to;

    let resolved = false;
    for (const loser of losers) {
      const key = loser.task.id as string;
      const calendar = context.calendars.forTask(loser.task);
      const target = snapToNextWorkingTime(calendar, cursor);
      if (!isAfter(target, loser.scheduled.start)) continue;
      const extra = workingMinutesBetween(calendar, loser.scheduled.start, target) as number;
      if (extra <= 0) continue;
      if (
        context.options.levelWithinSlack &&
        extra > (loser.scheduled.totalFloat as number) + OVERALLOCATION_EPSILON
      ) {
        continue;
      }
      forced.set(key, target);
      touchedTasks.add(key);
      resolved = true;
      // 估算此任務的完成時間，讓下一個讓路者接在其後（實際位置仍由 schedule() 決定）。
      const duration = loser.task.durationMinutes as number;
      cursor = duration > 0 ? addWorkingMinutes(calendar, target, minutes(duration)) : target;
    }
    if (resolved) {
      handledResources.add(resourceKey);
      progressed = true;
    } else {
      blocked.push(resourceKey);
    }
  }
  return { progressed, blocked };
}

/** 任務在某資源上的投入單位總和。 */
function unitsOnResource(context: Context, taskId: string, resourceId: ResourceId): number {
  let total = 0;
  for (const assignment of context.assignmentsByTask.get(taskId) ?? []) {
    if (assignment.resourceId !== resourceId) continue;
    total += assignment.units as number;
  }
  return total;
}

/**
 * 執行資源撫平。
 *
 * 演算法：反覆「排程 → 偵測超額配置 → 依排序準則延後讓路者」，直到沒有超額
 * 配置、無法再進展、或達到迭代上限為止。任務工期與相依關係一律不變，被延後的
 * 任務以 SNET 限制交由 `schedule()` 重新排程（因此後續任務會自動跟著移動）。
 *
 * **不拋 `LevelingFailedError`**：`levelWithinSlack` 下浮時不足、或達到迭代上限
 * 時，一律不延後並以 `remainingOverallocations` + Diagnostic
 * （`RESOURCE_OVERALLOCATED` / `LEVELING_ITERATION_LIMIT`）如實回報。
 *
 * 邊界：延遲量只能持久化在 `Assignment.levelingDelay`（`Task` 無此欄位）。
 * 因此沒有任何指派的任務不會被記錄延遲——它本來就不會被資源衝突直接延後，
 * 其位移是後續任務跟著 `schedule()` 重算的結果，不需要儲存。
 */
export function level(input: LevelingInput): LevelingResult {
  const project = input.project;
  const options = input.options;
  const diagnostics: Diagnostic[] = [];
  const context = buildContext(project, options);

  const baseSchedule = input.schedule ?? schedule({ project });
  const baseById = scheduleMap(baseSchedule);

  if (options.canLevelAssignments) {
    diagnostics.push({
      severity: 'Info',
      code: 'ASSIGNMENT_LEVELING_UNSUPPORTED',
      message:
        'canLevelAssignments is not supported yet; leveling was performed at task level only.',
    });
  }

  // --- 既有 levelingDelay 的續用 ---
  const forced = new Map<string, ISODateTime>();
  if (!options.clearPreviousLeveling) {
    for (const assignment of project.assignments) {
      const delay = assignment.levelingDelay;
      if (delay === undefined || (delay as number) <= 0) continue;
      const task = context.tasksById.get(assignment.taskId as string);
      const scheduled = baseById.get(assignment.taskId as string);
      if (task === undefined || scheduled === undefined) continue;
      if (!isMovable(task, options)) continue;
      const at = addWorkingMinutes(
        context.calendars.forTask(task),
        scheduled.earlyStart,
        minutes(delay as number),
      );
      const existing = forced.get(task.id as string);
      forced.set(task.id as string, existing === undefined ? at : later(existing, at));
    }
  }

  const limit = levelingIterationLimit(project.tasks.length);
  const noSplits = new Map<string, readonly TaskSegment[]>();

  let result = forced.size === 0 ? baseSchedule : schedule({ project: withForcedStarts(project, forced) });
  let placements = buildPlacements(project, windowsFromSchedule(result, noSplits));
  let remaining = filterOverallocations(
    context,
    findOverallocations(project, placements, options.granularity, context.calendars),
  );

  let iterations = 0;
  let hitLimit = false;
  while (remaining.length > 0) {
    if (iterations >= limit) {
      hitLimit = true;
      break;
    }
    iterations += 1;
    const outcome = resolveRound(context, remaining, scheduleMap(result), baseById, forced);
    if (!outcome.progressed) break;
    result = schedule({ project: withForcedStarts(project, forced) });
    placements = buildPlacements(project, windowsFromSchedule(result, noSplits));
    remaining = filterOverallocations(
      context,
      findOverallocations(project, placements, options.granularity, context.calendars),
    );
  }

  // --- 任務切分（填補延後任務前方的空檔） ---
  const splits = new Map<string, readonly TaskSegment[]>();
  if (options.canSplitTasks && forced.size > 0) {
    applySplits(context, baseById, result, placements, forced, splits, diagnostics);
    if (splits.size > 0) {
      placements = buildPlacements(project, windowsFromSchedule(result, splits));
      remaining = filterOverallocations(
        context,
        findOverallocations(project, placements, options.granularity, context.calendars),
      );
    }
  }

  diagnostics.push({
    severity: 'Info',
    code: 'LEVELING_ITERATIONS',
    message: `Resource leveling performed iterations=${iterations} (limit ${limit}).`,
  });
  if (hitLimit) {
    diagnostics.push({
      severity: 'Warning',
      code: 'LEVELING_ITERATION_LIMIT',
      message: `Resource leveling stopped after ${iterations} iterations (limit ${limit}); ${remaining.length} overallocation(s) remain.`,
    });
  }
  for (const entry of remaining) {
    diagnostics.push({
      severity: 'Warning',
      code: 'RESOURCE_OVERALLOCATED',
      message:
        `Resource ${entry.resourceId as string} is still overallocated between ${entry.from as string} and ` +
        `${entry.to as string} (${(entry.demandUnits as number).toFixed(3)} of ${(entry.capacityUnits as number).toFixed(3)} units).`,
      resourceId: entry.resourceId,
    });
  }

  // --- 產出調整清單 ---
  const finalById = scheduleMap(result);
  const adjustments: LevelingAdjustment[] = [];
  const adjustedIds = new Set<string>([...forced.keys(), ...splits.keys()]);
  for (const task of project.tasks) {
    const key = task.id as string;
    if (!adjustedIds.has(key)) continue;
    const scheduled = finalById.get(key);
    const base = baseById.get(key);
    if (scheduled === undefined || base === undefined) continue;
    const segments = splits.get(key) ?? [{ start: scheduled.start, finish: scheduled.finish }];
    const first = segments[0];
    const last = segments[segments.length - 1];
    if (first === undefined || last === undefined) continue;
    const calendar = context.calendars.forTask(task);
    const delay = workingMinutesBetween(calendar, base.earlyStart, first.start) as number;
    if (delay <= 0 && segments.length < 2) continue;
    adjustments.push({
      taskId: task.id,
      levelingDelay: minutes(Math.max(0, delay)),
      newStart: first.start,
      newFinish: last.finish,
      segments,
    });
  }

  const finishDelay = workingMinutesBetween(
    context.calendars.project,
    baseSchedule.projectFinish,
    result.projectFinish,
  ) as number;

  return {
    adjustments,
    assignmentAdjustments: [],
    remainingOverallocations: remaining,
    projectFinish: result.projectFinish,
    finishDelay: minutes(Math.max(0, finishDelay)),
    diagnostics,
  };
}

// ---------------------------------------------------------------------------
// 任務切分
// ---------------------------------------------------------------------------

/** 依日彙總的資源需求（分鐘）。 */
type DemandByDay = Map<string, Map<number, number>>;

function buildDemandByDay(context: Context, placements: readonly Placement[]): DemandByDay {
  const demand: DemandByDay = new Map();
  for (const placement of placements) {
    const resource = context.resourcesById.get(placement.resourceId as string);
    if (resource === undefined) continue;
    const calendar = context.calendars.forResource(resource);
    let byDay = demand.get(placement.resourceId as string);
    if (byDay === undefined) {
      byDay = new Map<number, number>();
      demand.set(placement.resourceId as string, byDay);
    }
    for (const segment of placement.segments) {
      const from = parseDateTime(segment.start);
      const to = parseDateTime(segment.finish);
      for (let day = from.day; day <= to.day; day += 1) {
        const low = day === from.day ? formatDateTime(day, from.minute) : formatDateTime(day, 0);
        const high = day === to.day ? formatDateTime(day, to.minute) : formatDateTime(day + 1, 0);
        const worked = workingMinutesBetween(calendar, low, high) as number;
        if (worked <= 0) continue;
        byDay.set(day, (byDay.get(day) ?? 0) + worked * placement.units);
      }
    }
  }
  return demand;
}

const MAX_SPLIT_DAYS = 3660;

/**
 * 對被延後的任務嘗試「填補前方空檔」：在不違反相依關係、不製造新的超額配置、
 * 且不晚於撫平後完成日的前提下，把工作切成多段提前進行。總工時不變。
 */
function applySplits(
  context: Context,
  baseById: ReadonlyMap<string, ScheduledTask>,
  result: ScheduleResult,
  placements: readonly Placement[],
  forced: ReadonlyMap<string, ISODateTime>,
  splits: Map<string, readonly TaskSegment[]>,
  diagnostics: Diagnostic[],
): void {
  const finalById = scheduleMap(result);
  const demand = buildDemandByDay(context, placements);
  const placementsByTask = new Map<string, Placement[]>();
  for (const placement of placements) {
    const key = placement.taskId as string;
    const bucket = placementsByTask.get(key);
    if (bucket === undefined) placementsByTask.set(key, [placement]);
    else bucket.push(placement);
  }

  for (const key of [...forced.keys()].sort()) {
    const task = context.tasksById.get(key);
    const scheduled = finalById.get(key);
    const base = baseById.get(key);
    if (task === undefined || scheduled === undefined || base === undefined) continue;
    const own = placementsByTask.get(key) ?? [];
    if (own.length === 0) continue;
    if (task.constraintDate !== null && task.constraintType !== 'ASAP') continue;

    // 相依關係下界：任何一段工作都不得早於此時間點。
    let floor = base.earlyStart;
    let dependencyOk = true;
    for (const dependency of context.project.dependencies) {
      if ((dependency.successorId as string) !== key) continue;
      if ((dependency.lagMinutes as number) !== 0) {
        dependencyOk = false;
        break;
      }
      const predecessor = finalById.get(dependency.predecessorId as string);
      if (predecessor === undefined) continue;
      if (dependency.type === 'FS') floor = later(floor, predecessor.finish);
      else if (dependency.type === 'SS') floor = later(floor, predecessor.start);
      else {
        dependencyOk = false;
        break;
      }
    }
    if (!dependencyOk) continue;

    // 先移除本任務目前的需求，再測試各日是否有餘裕。
    for (const placement of own) {
      const resource = context.resourcesById.get(placement.resourceId as string);
      if (resource === undefined) continue;
      const byDay = demand.get(placement.resourceId as string);
      const calendar = context.calendars.forResource(resource);
      if (byDay === undefined) continue;
      for (const segment of placement.segments) {
        const from = parseDateTime(segment.start);
        const to = parseDateTime(segment.finish);
        for (let day = from.day; day <= to.day; day += 1) {
          const low = day === from.day ? formatDateTime(day, from.minute) : formatDateTime(day, 0);
          const high = day === to.day ? formatDateTime(day, to.minute) : formatDateTime(day + 1, 0);
          const worked = workingMinutesBetween(calendar, low, high) as number;
          if (worked <= 0) continue;
          byDay.set(day, (byDay.get(day) ?? 0) - worked * placement.units);
        }
      }
    }

    const dayAllowed = (day: number): boolean => {
      for (const placement of own) {
        const resource = context.resourcesById.get(placement.resourceId as string);
        if (resource === undefined) return false;
        const calendar = context.calendars.forResource(resource);
        const dayStart = formatDateTime(day, 0);
        const dayEnd = formatDateTime(day + 1, 0);
        const capacityMinutes = workingMinutesBetween(calendar, dayStart, dayEnd) as number;
        if (capacityMinutes <= 0) return false;
        const capacityUnits = availableUnitsOnDay(context, placement.resourceId as string, day);
        const used = demand.get(placement.resourceId as string)?.get(day) ?? 0;
        const required = used + placement.units * capacityMinutes;
        if (required > capacityUnits * capacityMinutes + OVERALLOCATION_EPSILON) return false;
      }
      return true;
    };

    // fillSegments 只在「工期完整放置成功」時回傳結果；部分結果一律放棄，
    // 否則被切掉的那段工時會憑空消失。
    const segments = fillSegments(context, task, floor, dayAllowed);
    const last = segments === null ? undefined : segments[segments.length - 1];
    const first = segments === null ? undefined : segments[0];
    const accepted =
      segments !== null &&
      first !== undefined &&
      last !== undefined &&
      !isAfter(last.finish, scheduled.finish) &&
      (segments.length > 1 || isBefore(first.start, scheduled.start)) &&
      preservesWork(context, own, segments);

    const applied: readonly TaskSegment[] =
      accepted && segments !== null
        ? segments
        : [{ start: scheduled.start, finish: scheduled.finish }];
    if (accepted && segments !== null) {
      splits.set(key, segments);
      if (segments.length > 1) {
        diagnostics.push({
          severity: 'Info',
          code: 'TASK_SPLIT',
          message: `Task ${key} was split into ${segments.length} segments to fill idle resource capacity.`,
          taskId: task.id,
        });
      }
    }

    // 把（切分後或原本的）需求加回去，供後續任務判斷。
    for (const placement of own) {
      const resource = context.resourcesById.get(placement.resourceId as string);
      if (resource === undefined) continue;
      const calendar = context.calendars.forResource(resource);
      let byDay = demand.get(placement.resourceId as string);
      if (byDay === undefined) {
        byDay = new Map<number, number>();
        demand.set(placement.resourceId as string, byDay);
      }
      for (const segment of applied) {
        const from = parseDateTime(segment.start);
        const to = parseDateTime(segment.finish);
        for (let day = from.day; day <= to.day; day += 1) {
          const low = day === from.day ? formatDateTime(day, from.minute) : formatDateTime(day, 0);
          const high = day === to.day ? formatDateTime(day, to.minute) : formatDateTime(day + 1, 0);
          const worked = workingMinutesBetween(calendar, low, high) as number;
          if (worked <= 0) continue;
          byDay.set(day, (byDay.get(day) ?? 0) + worked * placement.units);
        }
      }
    }
  }
}

function availableUnitsOnDay(context: Context, resourceId: string, day: number): number {
  const resource = context.resourcesById.get(resourceId);
  if (resource === undefined) return 0;
  const date = (formatDateTime(day, 0) as string).slice(0, 10);
  let value: number | null = null;
  for (const window of resource.availability) {
    if ((window.from as string) > date) continue;
    if (window.to !== null && (window.to as string) < date) continue;
    const candidate = window.maxUnits as number;
    value = value === null ? candidate : Math.min(value, candidate);
  }
  return value ?? (resource.maxUnits as number);
}

/**
 * 檢查切分後每一筆指派的工時是否維持不變。
 *
 * 工期以「任務日曆」計算，工時以「資源日曆」計算；兩者不同時，把工作換到別的
 * 日子可能同時改變工時（例如任務日曆有週六、資源日曆沒有）。這裡以資源日曆為
 * 準逐筆比對切分前後的分鐘數，不相等就放棄切分。
 */
function preservesWork(
  context: Context,
  placements: readonly Placement[],
  segments: readonly TaskSegment[],
): boolean {
  const measure = (
    resourceId: string,
    windows: readonly TaskSegment[],
  ): number | null => {
    const resource = context.resourcesById.get(resourceId);
    if (resource === undefined) return null;
    const calendar = context.calendars.forResource(resource);
    let total = 0;
    for (const window of windows) {
      total += workingMinutesBetween(calendar, window.start, window.finish) as number;
    }
    return total;
  };
  for (const placement of placements) {
    const before = measure(placement.resourceId as string, placement.segments);
    const after = measure(placement.resourceId as string, segments);
    if (before === null || after === null) return false;
    if (Math.abs(before - after) > OVERALLOCATION_EPSILON) return false;
  }
  return true;
}

/**
 * 自 `floor` 起，只在 `dayAllowed` 為真的日子填入工期，回傳連續區段。
 * 工期無法在掃描上限內**完整**放置時回傳 `null`（呼叫端必須放棄切分），
 * 絕不回傳只放了一部分的區段。
 */
function fillSegments(
  context: Context,
  task: Task,
  floor: ISODateTime,
  dayAllowed: (day: number) => boolean,
): readonly TaskSegment[] | null {
  const calendar = context.calendars.forTask(task);
  let remaining = task.durationMinutes as number;
  if (remaining <= 0) return [{ start: floor, finish: floor }];

  let cursor = snapToNextWorkingTime(calendar, floor);
  const segments: TaskSegment[] = [];
  for (let guard = 0; guard < MAX_SPLIT_DAYS && remaining > 0; guard += 1) {
    const point = parseDateTime(cursor);
    const dayEnd = formatDateTime(point.day + 1, 0);
    if (!dayAllowed(point.day)) {
      cursor = snapToNextWorkingTime(calendar, dayEnd);
      continue;
    }
    const availableToday = workingMinutesBetween(calendar, cursor, dayEnd) as number;
    if (availableToday <= 0) {
      cursor = snapToNextWorkingTime(calendar, dayEnd);
      continue;
    }
    const take = Math.min(availableToday, remaining);
    const finish = addWorkingMinutes(calendar, cursor, minutes(take));
    const previous = segments[segments.length - 1];
    // 兩段之間若沒有任何工作分鐘（跨夜／跨週末），視為同一段。
    if (
      previous !== undefined &&
      (workingMinutesBetween(calendar, previous.finish, cursor) as number) === 0
    ) {
      segments[segments.length - 1] = { start: previous.start, finish };
    } else {
      segments.push({ start: cursor, finish });
    }
    remaining -= take;
    if (remaining <= 0) break;
    cursor = snapToNextWorkingTime(calendar, dayEnd);
  }
  return remaining > 0 ? null : segments;
}

// ---------------------------------------------------------------------------
// 公開輔助函式
// ---------------------------------------------------------------------------

/**
 * 僅偵測超額配置，不做任何調整。
 *
 * 判定方式：逐日、逐「相鄰端點區間」比對**實際同時佔用的單位數**與該日可用量，
 * `demandUnits` 回報區間內的尖峰值，`capacityUnits` 回報以工作分鐘數加權的平均
 * 可用量。`granularity` 只決定**回報／合併的區間長度**（Minute / Hour / Day /
 * Week / Month），不影響「是否構成超額」的判定。
 *
 * ⚠️ **刻意偏離 MS Project 的行為（不是未實作）**：MS Project 的
 * 「look for overallocations on a day-by-day / week-by-week basis」會在該期間內
 * 取平均，較粗的粒度因此會**容忍**期間內的尖峰（例如兩個各佔兩天的任務完全重疊，
 * 在週基準下平均為 0.8 units 而不報）。本實作不這麼做：那會產生偽陰性——真實存在
 * 的重疊完全不被告知，使用者連問題存在都不知道；相對地偽陽性至少是可被判讀後略過的。
 * 經 2026-08 決議採「一律偵測實際重疊」。
 *
 * 未來若需要恢復 MS Project 的容忍語義，請**新增獨立選項**（例如
 * `toleranceBasis: 'None' | 'Day' | 'Week'`），不要讓 `granularity` 同時承擔
 * 「回報區間」與「容忍度」兩種職責——兩用會讓「想要週報表」與「想要週容忍度」
 * 無法各自表達。
 */
export function detectOverallocations(
  project: Project,
  granularity: LevelingGranularity,
): readonly Overallocation[] {
  const calendars = new CalendarSet(project);
  const placements = buildPlacements(project, windowsFromProject(project));
  return findOverallocations(project, placements, granularity, calendars);
}

/**
 * 將撫平結果寫回專案（不可變更新）：任務日期以重新排程結果為準，
 * 被調整的任務把延遲量記在其指派的 `levelingDelay` 上，供下次撫平續用。
 */
export function applyLeveling(project: Project, result: LevelingResult): Project {
  if (result.adjustments.length === 0) return project;
  const delayByTask = new Map<string, Minutes>();
  const startByTask = new Map<string, ISODateTime>();
  const segmentsByTask = new Map<string, readonly TaskSegment[]>();
  for (const adjustment of result.adjustments) {
    delayByTask.set(adjustment.taskId as string, adjustment.levelingDelay);
    startByTask.set(adjustment.taskId as string, adjustment.newStart);
    segmentsByTask.set(adjustment.taskId as string, adjustment.segments);
  }

  const forced = new Map<string, ISODateTime>();
  const calendars = new CalendarSet(project);
  const tasksById = new Map<string, Task>();
  for (const task of project.tasks) tasksById.set(task.id as string, task);
  const baseById = scheduleMap(schedule({ project }));
  for (const [key, delay] of delayByTask) {
    const task = tasksById.get(key);
    const base = baseById.get(key);
    if (task === undefined || base === undefined) continue;
    if ((delay as number) <= 0) continue;
    forced.set(key, addWorkingMinutes(calendars.forTask(task), base.earlyStart, delay));
  }

  const leveled = applySchedule(project, schedule({ project: withForcedStarts(project, forced) }));
  const assignments = leveled.assignments.map((assignment) => {
    const key = assignment.taskId as string;
    const delay = delayByTask.get(key);
    if (delay === undefined) return assignment;
    const start = startByTask.get(key) ?? assignment.start;
    const segments = segmentsByTask.get(key);
    const last = segments?.[segments.length - 1];
    const next: Assignment = {
      ...assignment,
      levelingDelay: delay,
      start,
      finish: last?.finish ?? assignment.finish,
    };
    return next;
  });
  return { ...leveled, assignments };
}

/** 清除所有撫平延遲，回傳新的 Project（任務日期回到未撫平的 CPM 位置）。 */
export function clearLeveling(project: Project): Project {
  const assignments = project.assignments.map((assignment) => {
    if (assignment.levelingDelay === undefined) return assignment;
    const { levelingDelay: _removed, ...rest } = assignment;
    return rest;
  });
  const cleared: Project = { ...project, assignments };
  return applySchedule(cleared, schedule({ project: cleared }));
}
